import {
  AppBottomBar,
  AppFrame,
  AppHeader,
  AppSidebar,
  BottomBarItemContent,
  FOCUS_RING,
  HeaderBrand,
  Icon,
  ProfileMenu,
  SearchField,
  SidebarBrand,
  SidebarItemContent,
  SidebarTooltip,
  bottomBarItemClasses,
  cn,
  headerBrandClasses,
  sidebarBrandClasses,
  sidebarItemClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { useApolloClient } from '@apollo/client/react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import { signOut } from '../lib/session.js'
import { ProfileEditor } from '../features/auth/ProfileEditor.js'
import { useLanguageMenuItems } from './LanguageMenuItems.js'
import { useCurrentPageTitle } from './page-title.js'

/**
 * Where a nav item can point.
 *
 * A union of the actual route paths rather than `string`, because `Link` is
 * typed against the route tree and a `string` destination would have to be
 * cast to reach it. Spelling the seven out keeps the shell's own links
 * checked, so a renamed route breaks here rather than at runtime.
 */
export type NavTarget =
  | '/$language'
  | '/$language/practices'
  | '/$language/practices/new'
  | '/$language/practices/settings'
  | '/$language/students'
  | '/$language/plan'
  | '/$language/learn'
  | '/$language/learn/progress'
  | '/$language/learn/history'
  | '/$language/learn/requests'

export interface NavEntry {
  to: NavTarget
  label: string
  icon: ReactNode
  /** A sidebar subpage; its parent remains the mobile navigation destination. */
  parent?: NavTarget
}

export interface PinnedAction extends NavEntry {
  /** The bottom bar is a fifth of the width the sidebar has, so it can take a shorter label. */
  shortLabel?: string
}

export interface AppShellProps {
  viewer: { name: string; email: string; avatarUrl?: string | null | undefined }
  /** Where this account type can go. Nothing is shared between tutor and student. */
  nav: readonly NavEntry[]
  /**
   * Where the brand links to: this account type's own home. The tutor's
   * dashboard, the student's practice list.
   *
   * Its own prop rather than `nav[0]`, even though home happens to be first
   * for both today. Reading it off the array would make the logo's
   * destination a silent consequence of the order the navigation is listed
   * in, and someone reordering the nav would move the logo without meaning to.
   */
  home: NavTarget
  /** The tutor's Create Practice. A student creates nothing, so it is optional. */
  pinnedAction?: PinnedAction
  /** Header search, for the account type that has something to search. */
  search?: { defaultValue: string; onSubmit: (query: string) => void }
  children: ReactNode
}

/**
 * App shell (handoff, Organisms).
 *
 * `[sidebar 260px] [main: header 64px + scrolling work area]`. The sidebar and
 * the header are fixed; the work area is the only thing that scrolls.
 *
 * The destinations arrive as props rather than living here, because the two
 * account types share the frame and nothing inside it: a student has no
 * Students list and nothing to create. Sign out is the one item that is the
 * same either way, so it is the one item written into the shell.
 *
 * Three widths, as the handoff's responsive table sets them out: a bottom bar
 * below 768, a 72px icon rail to 1119, the full 260px sidebar above that. The
 * 1120 boundary is not one of Tailwind's defaults, so it is written out.
 */
/**
 * Which destination reads as current: the *longest* one the path matches, not
 * the first.
 *
 * `/learn/history` starts with `/learn`, so a plain prefix test lights up Home
 * and History together and the nav stops answering "where am I". Matching the
 * longest prefix keeps a section lit from its own sub-pages — `/practices/new`
 * still lights Practices — while a nested destination of its own wins
 * outright. Exported because it is the whole rule, and a rule is worth testing
 * without a router around it.
 *
 * Both arguments are resolved paths — `/es/practices`, not
 * `/$language/practices`. The caller substitutes the language first, which is
 * what keeps this a string comparison and not a route matcher.
 */
export function activeDestination(
  pathname: string,
  destinations: readonly string[],
): string | undefined {
  return destinations
    .filter((to) =>
      to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`),
    )
    .sort((a, b) => b.length - a.length)[0]
}

/**
 * How the router's `Link` decides a destination is active, for the shell's own
 * links: exactly, never by prefix.
 *
 * `Link` sets `aria-current="page"` itself on an active link, and by default
 * "active" is a prefix match. The tutor's home is `/es`, a prefix of every
 * route, so the Dashboard link announced itself as the current page from
 * every screen, alongside the one that really was — and the bottom bar,
 * which marks `aria-current` visibly, drew two current places. Exact leaves
 * `Link` agreeing with `activeDestination`, which is what decides.
 */
const EXACT = { exact: true } as const

export function AppShell({ viewer, nav, home, pinnedAction, search, children }: AppShellProps) {
  const t = useTranslate()
  const languageItems = useLanguageMenuItems()
  const [editingProfile, setEditingProfile] = useState(false)
  const [expandedNav, setExpandedNav] = useState<Partial<Record<NavTarget, boolean>>>({})
  const client = useApolloClient()
  const title = useCurrentPageTitle()
  // The i18n provider sits above every route that renders a shell, so this
  // is the same value the URL carries — and one that cannot be undefined.
  const language = useLanguage()
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  /**
   * A route path with the language filled in.
   *
   * Only for the active-destination comparison below, which is a string test
   * against the browser's pathname and so needs `/es/practices` rather than
   * the template. The `Link`s themselves pass the template plus `params`, and
   * the router does this substitution properly.
   */
  const resolve = (to: NavTarget) => `/${language}${to.slice('/$language'.length)}`

  /**
   * The current destination is the *longest* one the path matches, not the
   * first.
   *
   * `/learn/history` starts with `/learn`, so a plain prefix test lights up
   * Home and History together and the nav stops answering "where am I".
   * Matching on the longest prefix keeps a section highlighted from its own
   * sub-pages — `/practices/new` still lights Practices — while a nested
   * destination of its own wins outright.
   */
  const destinations = (
    pinnedAction ? [pinnedAction.to, ...nav.map((e) => e.to)] : nav.map((e) => e.to)
  ).map(resolve)
  const current = activeDestination(pathname, destinations)

  function isActive(to: NavTarget) {
    return resolve(to) === current
  }

  const bottomBar: NavEntry[] = pinnedAction
    ? [
        { ...pinnedAction, label: pinnedAction.shortLabel ?? pinnedAction.label },
        ...nav.filter((entry) => !entry.parent),
      ]
    : [...nav.filter((entry) => !entry.parent)]

  // The frame, the sidebar, the rail and the bottom bar are the shared parts
  // in `@athena/ui` (organisms/AppFrame), which the administration composes
  // too; what is in them — the pinned action, the header's title and search —
  // is the product's own.
  const sidebar = (
    <AppSidebar label={t('navigation.main')}>
      {/* The brand, above the navigation.

         `reverse` in both forms, because the sidebar is indigo: the colour
         artwork's own indigo plane would vanish into it, and the light
         lockup's near-black wordmark would sit at 1.8:1.

         Which form is a question of width, and the answer is the same one
         the nav labels below give. The full sidebar is 260px, so the
         lockup has 228px to sit in and takes 150 of them. The rail is
         72px — 40px inside the padding — where a 300x64 lockup could only
         fit by shrinking the wordmark to nothing, so the rail gets the
         mark instead.

         Decorative: this nav is already a landmark named "Main", so a logo
         announced inside it would be the word "Athena" twice. */}
      <Link
        to={home}
        params={{ language }}
        /**
         * Exact, or this link is "active" on every page in the app.
         *
         * `Link` matches by prefix by default, and the tutor's home is
         * `/es` — a prefix of every route there is. Without this the brand
         * would carry `aria-current="page"` from the students list, which is
         * the nav telling a screen-reader user they are somewhere they are
         * not. The nav items below dodge it by computing their own active
         * state through `activeDestination`; this is the equivalent.
         *
         * On home itself it does mark itself current, alongside the nav
         * item. That one is left alone: `Link` spreads its active props last
         * — `...isActive && STATIC_ACTIVE_PROPS` — so no prop can override
         * it, and two links that really do point at the current page saying
         * so beats a ref reaching into the DOM to unsay it.
         */
        activeOptions={{ exact: true }}
        className={sidebarBrandClasses}
      >
        {/* The on-dark artwork, chosen by the indigo it sits on. Its name is
           "Athena home" rather than the nav item's own wording: a link
           should say where it goes, and naming it "Dashboard" would put two
           links of that name inside one landmark. */}
        <SidebarBrand label={t('navigation.brandHome')} />
      </Link>

      {pinnedAction ? (
        <SidebarTooltip label={pinnedAction.label}>
          <Link
            to={pinnedAction.to}
            params={{ language }}
            // Exact, like every destination below: see `EXACT`.
            activeOptions={EXACT}
            className={cn(
              'mb-4 flex h-control-lg items-center justify-center gap-2 rounded-sm',
              'border-[1.5px] border-border-strong bg-surface text-button font-semibold text-text transition-colors',
              'hover:bg-surface-secondary hover:border-text-disabled',
              FOCUS_RING,
            )}
          >
            <span aria-hidden="true" className="flex size-icon-md shrink-0 items-center">
              {pinnedAction.icon}
            </span>
            <span className="hidden desktop:inline">{pinnedAction.label}</span>
            <span className="sr-only desktop:hidden">{pinnedAction.label}</span>
          </Link>
        </SidebarTooltip>
      ) : null}

      {nav.map((entry) => {
        if (entry.parent && !expandedNav[entry.parent]) return null
        const hasChildren = nav.some((child) => child.parent === entry.to)
        return (
          <div key={entry.to} className="relative">
            <SidebarTooltip label={entry.label}>
              <Link
                to={entry.to}
                params={{ language }}
                activeOptions={EXACT}
                aria-current={isActive(entry.to) ? 'page' : undefined}
                onClick={
                  hasChildren
                    ? () => setExpandedNav((previous) => ({ ...previous, [entry.to]: true }))
                    : undefined
                }
                className={cn(
                  sidebarItemClasses(isActive(entry.to)),
                  'whitespace-nowrap',
                  hasChildren && 'desktop:pr-11',
                  entry.parent && 'relative desktop:pl-[58px]',
                )}
              >
                {entry.parent ? (
                  <>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-[30px] top-0 hidden h-1/2 w-[14px] rounded-bl-sm border-b border-l border-primary-border/50 desktop:block"
                    />
                    <span className="desktop:hidden">
                      <SidebarItemContent icon={entry.icon} label={entry.label} />
                    </span>
                    <span className="hidden desktop:inline">{entry.label}</span>
                  </>
                ) : (
                  <SidebarItemContent icon={entry.icon} label={entry.label} />
                )}
              </Link>
            </SidebarTooltip>
            {hasChildren ? (
              <button
                type="button"
                aria-label={entry.label}
                aria-expanded={Boolean(expandedNav[entry.to])}
                onClick={() =>
                  setExpandedNav((previous) => ({ ...previous, [entry.to]: !previous[entry.to] }))
                }
                className={cn(
                  'absolute right-2 top-2 hidden size-9 items-center justify-center rounded-sm text-primary-border hover:bg-primary/40 hover:text-surface desktop:flex',
                  FOCUS_RING,
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'transition-transform duration-fast',
                    expandedNav[entry.to] && 'rotate-180',
                  )}
                >
                  <Icon.ChevronDown />
                </span>
              </button>
            ) : null}
          </div>
        )
      })}
    </AppSidebar>
  )

  const bottomNav = (
    <AppBottomBar label={t('navigation.main')}>
      {bottomBar.map((entry) => (
        <Link
          key={entry.to}
          to={entry.to}
          params={{ language }}
          activeOptions={EXACT}
          aria-current={
            isActive(entry.to) ||
            nav.some((child) => child.parent === entry.to && isActive(child.to))
              ? 'page'
              : undefined
          }
          className={bottomBarItemClasses(
            isActive(entry.to) ||
              nav.some((child) => child.parent === entry.to && isActive(child.to)),
          )}
        >
          <BottomBarItemContent icon={entry.icon} label={entry.label} />
        </Link>
      ))}
    </AppBottomBar>
  )

  return (
    <AppFrame
      sidebar={sidebar}
      bottomBar={bottomNav}
      mainClassName="mx-auto w-full max-w-container-max px-4 py-6 md:px-6"
      header={
        <AppHeader
          className={cn(
            'items-center',
            search
              ? 'grid min-h-header grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 gap-y-2 py-2 md:flex md:h-header md:gap-4 md:py-0'
              : 'flex h-header gap-4',
          )}
        >
          {/* The page's one visible h1, at every width. The title shares the
              first mobile row with the brand; on tutor screens the search and
              profile control form a second row. That keeps all three jobs
              legible instead of hiding the title or squeezing four controls
              into 390px. The inner span owns truncation so a long loaded title
              cannot push the account control off screen. */}
          {/* Below `md` the sidebar is replaced by the bottom bar, which has
              no room to sign itself, so the brand appears here instead — the
              same mark, in colour for the white header. It is not a second
              placement so much as the one placement following the chrome that
              is actually on screen. */}
          <Link
            to={home}
            params={{ language }}
            // Exact, for the reason the sidebar's copy spells out.
            activeOptions={{ exact: true }}
            className={headerBrandClasses}
          >
            <HeaderBrand label={t('navigation.brandHome')} />
          </Link>

          {/* Focusable from script only: a navigation lands here, so the new
              page is announced by name (see `RouteOrientation`). A place, not
              a control, so no ring and no Tab stop. */}
          <h1
            tabIndex={-1}
            className={cn('min-w-0 outline-none', search ? 'col-span-2 md:flex-1' : 'flex-1')}
          >
            <span className="block truncate text-h3 font-semibold text-text">{title}</span>
          </h1>

          {/* The one search field in the app. The handoff puts search in the
              header, so a screen that filters a list reads the query out of
              the URL rather than growing a second box of its own. */}
          {search ? (
            // Narrower until `desktop`, so at a tablet's width the search
            // gives the page title room instead of truncating it to a word.
            <div className="col-span-2 row-start-2 w-full min-w-0 md:ml-auto md:max-w-56 desktop:max-w-72">
              <SearchField
                defaultValue={search.defaultValue}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  search.onSubmit(event.currentTarget.value.trim())
                }}
              />
            </div>
          ) : null}

          <ProfileMenu
            // With no search field ahead of it, the menu is what pushes right.
            className={search ? 'col-start-3 row-start-2 md:col-auto md:row-auto' : 'ml-auto'}
            name={viewer.name}
            email={viewer.email}
            avatarUrl={viewer.avatarUrl}
            /**
             * Language first, sign out last.
             *
             * The selector is a group of items in the menu that already
             * exists rather than a control of its own in the header
             * (V1.md section 18): changing language is a rare, deliberate act
             * that belongs beside the account, not a fourth thing competing
             * with the page title and the search box.
             */
            groups={[{ id: 'language', label: t('language.menuLabel') }]}
            items={[
              { id: 'profile', label: t('profile.title'), onSelect: () => setEditingProfile(true) },
              ...languageItems,
              {
                id: 'sign-out',
                label: t('navigation.signOut'),
                // Better Auth owns the session, so signing out is its endpoint
                // rather than a mutation (V1.md section 5) — but it is a POST,
                // and there is a cache to empty on the way out. Both live in
                // `lib/session.ts`; this menu item only presses the button.
                onSelect: () => void signOut(client, language),
              },
            ]}
          />
        </AppHeader>
      }
    >
      {children}
      {editingProfile ? <ProfileEditor open onOpenChange={setEditingProfile} /> : null}
    </AppFrame>
  )
}
